import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PagePath } from '../../enums/page-path.enum';
import { CostTab } from '../../models/app.model';
import { DataLayerEvent, DataLayerGlobalVariable, MAIL_ORDER_COSTS, RETAIL_COSTS } from '../../models/google-tag-manager.model';
import { HeaderAction, type HeaderLink } from '../../models/header.model';
import { LinkRoles } from '../../models/link.model';
import { AppStateService } from '../../services/app-state.service';
import { ConfigResourceService } from '../../services/config-resource.service';
import { DeepLinkingParamsStateService } from '../../services/deep-linking-params-state.service';
import { DemographicsStateService } from '../../services/demographics-state.service';
import { DrugsStateService } from '../../services/drugs-state.service';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { ModalService } from '../../services/modal.service';
import { NavigationService } from '../../services/navigation.service';
import { PharmaciesStateService } from '../../services/pharmacies-state.service';
import { PlansStateService } from '../../services/plans-state.service';
import { RuntimeStateService } from '../../services/runtime-state.service';
import { LinkComponent } from '../link/link.component';
import { ContactUsModalComponent } from '../modals/contact-us-modal/contact-us-modal.component';
import { UniversalNavContext } from './universal-nav.context';

@Component({
  selector: 'app-universal-nav',
  templateUrl: './universal-nav.component.html',
  styleUrls: ['./universal-nav.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatDialogModule,
    FormsModule,
    LinkComponent
  ]
})
export class UniversalNavComponent implements OnInit, OnDestroy {
  private readonly drugsStateService = inject(DrugsStateService);
  private readonly plansStateService = inject(PlansStateService);
  private readonly pharmaciesStateService = inject(PharmaciesStateService);
  private readonly demographicsStateService = inject(DemographicsStateService);
  private readonly deepLinkingParamsStateService = inject(DeepLinkingParamsStateService);
  private readonly appStateService = inject(AppStateService);
  private readonly runtimeStateService = inject(RuntimeStateService);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly configService = inject(ConfigResourceService);
  private readonly modalService = inject(ModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly universalNavContext = inject(UniversalNavContext);
  private readonly googleTagManagerService = inject(GoogleTagManagerService);

  headerLinks: HeaderLink[] = [];
  activeTab: string = CostTab.RETAIL_COST;
  hasDrugs: boolean = false;
  showPharmacyTypeTabs: boolean = false;
  contactLinkTitleText: string = '';
  retailCostText: string = '';
  mailOrderText: string = '';
  readonly LinkRoles = LinkRoles;
  readonly CostTab = CostTab;
  readonly headerActions: Record<HeaderAction, () => void> = {
    [HeaderAction.CONTACT_US]: () => {
      this.openContactUs();
    },
    [HeaderAction.START_OVER]: () => {
      this.startOver();
    }
  };
  private readonly subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.configService.configLoaded$.subscribe({
        next: loaded => {
          if (loaded) {
            this.headerLinks = this.configService.getProcessedResourceValue<HeaderLink[]>('Spa_Header_Links', [
              {
                title: 'Contact Us',
                tooltipText: 'Opens contact us modal',
                iconName: 'phone',
                action: HeaderAction.CONTACT_US,
                ordinal: 1,
                display: true
              },
              {
                title: 'Start over',
                iconName: 'refresh',
                action: HeaderAction.START_OVER,
                ordinal: 2,
                display: true
              }
            ]);
            this.retailCostText = this.configService.getProcessedResourceValue<string>('MDCRetailCostText', 'Retail costs');
            this.mailOrderText = this.configService.getProcessedResourceValue<string>('MDCMailOrderText', 'Mail order costs');
            this.cdr.markForCheck();
          }
        }
      })
    );

    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe({
        next: (event: NavigationEnd) => {
          this.showPharmacyTypeTabs =
            event.url === PagePath.ACCOUNT_PRESCRIPTIONS ||
            event.url === PagePath.ACCOUNT_COVERAGE ||
            event.url === PagePath.OPTIONS ||
            event.url === PagePath.ANNUAL_COST_REPORT;
          this.cdr.markForCheck();
        }
      })
    );

    this.subscriptions.add(
      this.drugsStateService.getSelectedDrugs$().subscribe({
        next: drugs => {
          this.hasDrugs = drugs?.length > 0;
          this.cdr.markForCheck();
        }
      })
    );

    this.subscriptions.add(
      this.appStateService.getActiveTab$().subscribe({
        next: activeTab => {
          this.activeTab = activeTab;
          this.cdr.markForCheck();
        }
      })
    );

    this.subscriptions.add(
      this.universalNavContext.onStartOver$().subscribe({
        next: () => {
          this.plansStateService.resetPlansState$();
          this.pharmaciesStateService.resetPharmaciesState$();
          this.demographicsStateService.resetDemographicsState$();
          this.drugsStateService.resetDrugsState$();
          this.appStateService.resetAppState$();
          this.deepLinkingParamsStateService.resetDeepLinkingParams$();
          this.runtimeStateService.resetRuntimeState$({ resetAuthStatus: false });
          this.navigationService.navigateToPagePath([PagePath.HOME]);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setActiveTab(tab: CostTab): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_DRUG_COST_TYPE_SWITCH, {
      [DataLayerGlobalVariable.DRUG_COST_TYPE]: tab === CostTab.MAIL_ORDER_COST ? MAIL_ORDER_COSTS : RETAIL_COSTS
    });
    this.appStateService.setActiveTab$(tab);
  }

  onClickHeaderLink(link: HeaderLink): void {
    if (link.action) {
      this.headerActions[link.action]?.();
    } else if (
      link.href &&
      link.href === this.configService.getProcessedResourceValue<string>('Spa_HeaderFooter_ContactUs_ExternalURL', '')
    ) {
      this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_NAV_CONTACT_US);
    }
  }

  openContactUs(): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_NAV_CONTACT_US);
    this.modalService.openModal(ContactUsModalComponent, { position: { top: '1.75rem' } });
  }

  startOver(): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_NAV_START_OVER);
    this.universalNavContext.startOver();
  }

  trackByLinkTitle(index: number, headerLink: HeaderLink): string {
    return headerLink.title;
  }
}
